import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Dropdown } from 'primereact/dropdown';
import ActiveFilters, { ActiveFilterChip } from '../../components/novarium/ActiveFilters';
import MoreFiltersToggle from '../../components/novarium/MoreFiltersToggle';
import PeriodField, { PeriodDateField } from '../../components/novarium/PeriodField';
import { PeriodChoice } from '../../components/novarium/periodRange';
import { VIRTUAL_PANEL_CLASS, VIRTUAL_SCROLL } from '../../components/novarium/virtualOptions';
import { ServiceStatisticsTemplateType } from '../../dto/ServiceStatisticsDTO';
import { FilterField, FilterSelection, FilterVisibility } from './filterOptions';
import InfoTip from '../../components/novarium/InfoTip';
import { modeMessageId, StatisticsMode } from './statisticsMode';

/** Lo que se puede quitar con un chip: tipo, especialidad, profesional, servicio o agenda y código. */
export type FilterKey = 'templateType' | 'specialityId' | 'professionalId' | 'serviceId' | 'priorityId';

export type FilterChip = ActiveFilterChip<FilterKey>;

/** Lo que hay en los filtros de la pantalla (las fechas pueden estar vacías mientras se escriben). */
export interface FiltersDraft extends FilterSelection {
    from: Date | null;
    to: Date | null;
    priorityId: number;
}

export type DateField = 'initialDate' | 'finalDate';

/** Tipo de agenda de los filtros: 0 = todos, 1 consultorios, 2 servicios. */
export type TemplateTypeFilter = 0 | ServiceStatisticsTemplateType;

export type { PeriodChoice };

export interface FilterOption {
    id: number;
    name: string;
}

export interface StatisticsFiltersProps {
    mode: StatisticsMode;
    draft: FiltersDraft;
    /** Errores de las fechas, para mostrar en cada campo. */
    errors: Partial<Record<DateField, string>>;
    /** Las opciones de los filtros ya llegaron: antes, los selects están deshabilitados. */
    optionsReady: boolean;
    templateTypeOptions: FilterOption[];
    specialityOptions: FilterOption[];
    professionalOptions: FilterOption[];
    serviceOptions: FilterOption[];
    /** Qué selectores se muestran: los que no aportan en el contexto desaparecen. */
    visible: FilterVisibility;
    period: PeriodChoice;
    /** "25/9 al 24/10": el rango del atajo elegido, en texto chico bajo «Período» (vacío con «Personalizado»). */
    rangeText: string;
    chips: FilterChip[];
    /** «Más filtros» (Tipo, Especialidad y Profesional) desplegado. */
    moreOpen: boolean;
    /** Cuántos de los filtros de «Más filtros» están aplicados. */
    moreActive: number;
    onToggleMore: () => void;
    onChangeFilter: (field: FilterField, value: number) => void;
    /** Una fecha completa y válida, escrita o elegida en el calendario (o `null` con «Limpiar»): se guarda y se aplica ya. */
    onDateChange: (field: DateField, value: Date | null) => void;
    /** Al salir de los campos de fecha o con Enter: vuelve a validar (no hace nada si no hay nada pendiente). */
    onDatesCommit: () => void;
    onPeriodChange: (choice: PeriodChoice) => void;
    onRemoveChip: (key: FilterKey) => void;
    onClear: () => void;
}

/**
 * Filtros de las estadísticas, todos al instante (no hay «Consultar»). A la vista, una sola fila: «Período» (un selector
 * con Hoy, Últimos 7 días, Últimos 30 días, Mes actual, Mes anterior y Personalizado, con el rango resuelto debajo),
 * «Servicio o agenda» (en «Mis agendas», «Agenda») y «Más filtros» (Tipo, Especialidad y Profesional). Las fechas Desde y
 * Hasta solo aparecen con «Personalizado» y se aplican en cuanto hay una fecha completa y válida (escrita o elegida en el
 * calendario: un solo gesto); los selects, al elegir.
 * Los selectores que no aportan en el contexto no se muestran (ver `filterVisibility`), y «Más filtros» tampoco si quedó
 * vacío. Debajo, los chips «Filtrando por», que muestran todo lo aplicado. El código de atención se filtra desde la pestaña
 * «Códigos».
 */
export default function StatisticsFilters({
    mode, draft, errors, optionsReady, templateTypeOptions, specialityOptions, professionalOptions, serviceOptions, visible,
    period, rangeText, chips, moreOpen, moreActive, onToggleMore, onChangeFilter, onDateChange, onDatesCommit, onPeriodChange,
    onRemoveChip, onClear,
}: StatisticsFiltersProps) {
    const intl = useIntl();

    // Estable entre renderizados: las fechas del período no se vuelven a dibujar con cada renderizado de la pantalla
    const handleDateChange = useCallback((field: PeriodDateField, value: Date | null) => {
        onDateChange(field === 'from' ? 'initialDate' : 'finalDate', value);
    }, [onDateChange]);
    const periodErrors = useMemo(() => ({ from: errors.initialDate, to: errors.finalDate }), [errors]);

    const showMore = visible.templateType || visible.specialityId || visible.professionalId;

    return (
        <section className="nv-section ss-filters" aria-label={intl.formatMessage({ id: 'ServicesStatisticsFilters' })}>
            <div className="nv-form-row ss-filter-row">
                <PeriodField idPrefix="ss" period={period} rangeText={rangeText} from={draft.from} to={draft.to}
                    errors={periodErrors} onDateChange={handleDateChange} onDatesCommit={onDatesCommit}
                    onPeriodChange={onPeriodChange} />
                {visible.serviceId && (
                    <div className="nv-field nv-w-m">
                        <label htmlFor="ss-service">{intl.formatMessage({ id: modeMessageId(mode, 'ServiceField') })}</label>
                        <Dropdown inputId="ss-service" value={draft.serviceId} options={serviceOptions}
                            optionLabel="name" optionValue="id" filter filterBy="name"
                            virtualScrollerOptions={VIRTUAL_SCROLL} panelClassName={VIRTUAL_PANEL_CLASS}
                            emptyFilterMessage={intl.formatMessage({ id: modeMessageId(mode, 'NoServices') })} disabled={!optionsReady}
                            onChange={e => onChangeFilter('serviceId', e.value)} />
                    </div>
                )}
                {showMore && (
                    <MoreFiltersToggle open={moreOpen} active={moreActive} controls="ss-more-filters" onToggle={onToggleMore} />
                )}
            </div>

            {showMore && moreOpen && (
                <div id="ss-more-filters" className="nv-form-row">
                    {visible.templateType && (
                        <div className="nv-field nv-w-s">
                            <label htmlFor="ss-type">{intl.formatMessage({ id: 'ServicesStatisticsTypeField' })}</label>
                            <Dropdown inputId="ss-type" value={draft.templateType} options={templateTypeOptions}
                                optionLabel="name" optionValue="id"
                                onChange={e => onChangeFilter('templateType', e.value)} />
                        </div>
                    )}
                    {visible.specialityId && (
                        <div className="nv-field nv-w-m">
                            <label htmlFor="ss-speciality">{intl.formatMessage({ id: 'ServicesStatisticsSpecialityField' })}</label>
                            <Dropdown inputId="ss-speciality" value={draft.specialityId} options={specialityOptions}
                                optionLabel="name" optionValue="id" filter filterBy="name"
                                emptyFilterMessage={intl.formatMessage({ id: 'ServicesStatisticsNoSpecialities' })} disabled={!optionsReady}
                                onChange={e => onChangeFilter('specialityId', e.value)} />
                        </div>
                    )}
                    {visible.professionalId && (
                        <div className="nv-field nv-w-m">
                            <label htmlFor="ss-professional">
                                {intl.formatMessage({ id: 'ServicesStatisticsProfessionalField' })}
                                <InfoTip text={intl.formatMessage({ id: 'ServicesStatisticsProfessionalHint' })} />
                            </label>
                            <Dropdown inputId="ss-professional" value={draft.professionalId} options={professionalOptions}
                                optionLabel="name" optionValue="id" filter filterBy="name"
                                virtualScrollerOptions={VIRTUAL_SCROLL} panelClassName={VIRTUAL_PANEL_CLASS}
                                emptyFilterMessage={intl.formatMessage({ id: 'ServicesStatisticsNoProfessionals' })} disabled={!optionsReady}
                                onChange={e => onChangeFilter('professionalId', e.value)} />
                        </div>
                    )}
                </div>
            )}

            <ActiveFilters chips={chips} onRemove={onRemoveChip} onClear={onClear} />
        </section>
    );
}
